import type { AxiosResponse } from 'axios';
import api from './api';
import type {
  AccountSavedType,
  AdminReadType,
  ErrorReportSearchType,
  ErrorReportType,
  IdentityLinkEvent,
  SysAdminType,
  WithContractedIdentity,
} from 'bc-model';

interface AdminSearchUsersResponse {
  users: WithContractedIdentity<AccountSavedType>[];
  total: number;
}

const retrieveErrorReports = async (
  start?: Date,
  end?: Date
): Promise<AxiosResponse<ErrorReportSearchType[]>> => {
  return api.get(`/api/admin/errorReports?start=${start?.toISOString()}&end=${end?.toISOString()}`);
};

const createErrorReport = async (
  errorReport: Omit<ErrorReportType, 'appVersion'>
): Promise<AxiosResponse<{ id: number }>> => {
  return api.post(`/api/admin/errorReports`, errorReport);
};

const updateErrorReport = async (
  reportId: number,
  reportedDescription: string
): Promise<AxiosResponse<void>> => {
  return api.post(`/api/admin/errorReports/${reportId}`, { reportedDescription });
};

interface AdminUserFilter {
  search: string;
  filter?: {
    field: keyof AccountSavedType;
    value: string | number | boolean | undefined;
  };
  orderBy?: keyof AccountSavedType;
  orderDirection?: 'asc' | 'desc';
  limit: number;
  offset: number;
}

const buildUserFilter = (partial: Partial<AdminUserFilter> = {}): AdminUserFilter => ({
  search: '',
  limit: 10,
  offset: 0,
  ...partial,
});

const toString = (value: string | number | boolean | undefined | null): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return Number(value).toString();
  return '';
};

type AdminUserAdditionalParams = {
  includeLockStatus?: boolean;
};

const formatQueryString = (
  filter: AdminUserFilter,
  additionalParams?: AdminUserAdditionalParams
): string => {
  const params = [
    ['q', filter.search],
    ['limit', toString(filter.limit)],
    ['offset', toString(filter.offset)],
  ];

  if (filter.orderBy) {
    params.push(['orderBy', filter.orderBy]);

    if (filter.orderDirection) {
      params.push(['order', filter.orderDirection]);
    } else {
      params.push(['order', 'desc']);
    }
  }

  if (filter.filter) {
    params.push(['filter', filter.filter.field]);
    params.push(['filter', toString(filter.filter.value)]);
  }

  if (additionalParams?.includeLockStatus) {
    params.push(['includeLockStatus', additionalParams.includeLockStatus.toString()]);
  }

  return new URLSearchParams(params).toString();
};

async function adminSearchUsers(
  filter: Partial<AdminUserFilter>,
  additionalParams?: AdminUserAdditionalParams
): Promise<AxiosResponse<AdminSearchUsersResponse>> {
  return api.get(
    `/api/admin/users?${formatQueryString(buildUserFilter(filter), additionalParams)}`
  );
}

async function adminResetPassword(accountId: number | string): Promise<AxiosResponse> {
  return api.post(`/api/admin/user/${accountId}`);
}

async function adminUnlockAccount(accountId: number | string): Promise<AxiosResponse> {
  return api.post(`/api/admin/user/${accountId}/unlock`);
}

async function adminChangeEmail(accountId: number | string, email: string): Promise<AxiosResponse> {
  return api.post(`/api/admin/user/${accountId}/email`, { email, accountId });
}

interface IdentityCurrentLink {
  ssoSub: string;
  linkedAt: string;
  provider: string;
}

interface UserIdentityHistoryResponse {
  events: IdentityLinkEvent[];
  currentIdentity: IdentityCurrentLink | null;
}

async function adminGetUserIdentityHistory(
  accountId: number | string
): Promise<AxiosResponse<UserIdentityHistoryResponse>> {
  return api.get(`/api/admin/user/${accountId}/identity/history`);
}

async function adminUnlinkIdentity(accountId: number | string): Promise<AxiosResponse> {
  return api.delete(`/api/admin/user/${accountId}/identity`);
}

async function adminDeactivateUser(accountId: number | string): Promise<AxiosResponse> {
  return api.delete(`/api/admin/user/${accountId}`);
}

async function adminReactivateUser(accountId: number | string): Promise<AxiosResponse> {
  return api.post(`/api/admin/user/${accountId}/reactivate`);
}

interface AdminsResponse {
  admins: WithContractedIdentity<AdminReadType>[];
}

async function retrieveAllAdmins(): Promise<AxiosResponse<AdminsResponse>> {
  return api.get('/api/admin/accounts');
}

interface SysAdminsResponse {
  sysAdmins: WithContractedIdentity<SysAdminType>[];
}

async function retrieveAllSysAdmins(): Promise<AxiosResponse<SysAdminsResponse>> {
  return api.get('/api/admin/sysAdmins');
}

async function createAdmin(accountId: number | string): Promise<AxiosResponse> {
  const body =
    typeof accountId === 'string' ? { accountUuid: accountId } : { accountId: accountId };
  return api.post('/api/admin/accounts', body);
}

async function updateAdmin(accountId: number | string, revoked: boolean): Promise<AxiosResponse> {
  return api.put(`/api/admin/accounts/${accountId}`, { revoked });
}

async function adminVerifyUser(
  accountId: number | string,
  status: boolean
): Promise<AxiosResponse> {
  const accountStatus = status ? 'approved' : 'denied';
  const idField =
    typeof accountId === 'string' ? { accountUuid: accountId } : { accountId: accountId };
  return api.post(`/api/admin/verifyUser`, { ...idField, accountStatus });
}

async function adminForceLogoutUser(accountId: number | string): Promise<AxiosResponse> {
  const body =
    typeof accountId === 'string' ? { accountUuid: accountId } : { accountId: accountId };
  return api.post(`/api/admin/force-logout`, body);
}

async function updateProductAdmin(
  accountId: number | string,
  isProductAdmin: boolean
): Promise<AxiosResponse> {
  return api.put(`/api/admin/accounts/${accountId}/product-admin`, { isProductAdmin });
}

interface BackfillVersion {
  version: number;
  name: string;
  md5: string;
  run_at: string;
  completed: boolean;
}

interface BackfillsResponse {
  backfills: BackfillVersion[];
  total: number;
}

async function retrieveBackfills(
  limit: number,
  offset: number
): Promise<AxiosResponse<BackfillsResponse>> {
  return api.get(`/api/admin/backfills?limit=${limit}&offset=${offset}`);
}

interface AdminJobInfo {
  id: string;
  name: string;
  description: string;
}

async function retrieveBackgroundJobs(): Promise<AxiosResponse<{ jobs: AdminJobInfo[] }>> {
  return api.get('/api/admin/jobs', { cache: false });
}

interface BackgroundJobRunRecord {
  uuid: string;
  jobId: string;
  status: 'queued' | 'running' | 'success' | 'failed' | 'terminating' | 'terminated';
  startedAt: string;
  completedAt: string | null;
  triggeredBy: number;
  triggeredByUuid: string | null;
  progressMessage: string | null;
  triggeredByName: string;
  report: Record<string, unknown> | null;
  canTerminate: boolean;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
}

async function startBackgroundJob(jobId: string): Promise<AxiosResponse<BackgroundJobRunRecord>> {
  return api.post(`/api/admin/jobs/${jobId}/runs`);
}

async function retrieveBackgroundJobRuns(
  jobId: string,
  limit?: number,
  offset?: number
): Promise<
  AxiosResponse<{ runs: BackgroundJobRunRecord[]; total: number; canTerminate: boolean }>
> {
  const params = new URLSearchParams();
  if (limit !== undefined) params.set('limit', String(limit));
  if (offset !== undefined) params.set('offset', String(offset));
  const qs = params.toString();
  return api.get(`/api/admin/jobs/${jobId}/runs${qs ? `?${qs}` : ''}`, { cache: false });
}

async function deleteBackgroundJobRun(runUuid: string): Promise<void> {
  await api.delete(`/api/admin/jobs/runs/${runUuid}`);
}

async function terminateBackgroundJobRun(
  runUuid: string
): Promise<AxiosResponse<BackgroundJobRunRecord>> {
  return api.post(`/api/admin/jobs/runs/${runUuid}/terminate`);
}

const TESTING_ACCESS = {
  formatQueryString,
};

export type {
  AdminSearchUsersResponse,
  AdminUserFilter,
  BackfillVersion,
  BackfillsResponse,
  AdminJobInfo,
  BackgroundJobRunRecord,
  UserIdentityHistoryResponse,
  IdentityCurrentLink,
};
export {
  adminForceLogoutUser,
  adminGetUserIdentityHistory,
  retrieveBackgroundJobs,
  startBackgroundJob,
  retrieveBackgroundJobRuns,
  deleteBackgroundJobRun,
  terminateBackgroundJobRun,
  adminSearchUsers,
  adminResetPassword,
  adminUnlockAccount,
  adminChangeEmail,
  adminUnlinkIdentity,
  adminDeactivateUser,
  adminReactivateUser,
  retrieveErrorReports,
  createErrorReport,
  updateErrorReport,
  createAdmin,
  updateAdmin,
  retrieveAllAdmins,
  retrieveAllSysAdmins,
  updateProductAdmin,
  adminVerifyUser,
  buildUserFilter,
  retrieveBackfills,
  TESTING_ACCESS,
};
